import { useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '../../../shared';
import type { TokenDef } from '../services/interviewLinksApi';
import { filterTokens } from './filterTokens';

/**
 * A rounded textarea with a "/" slash menu for inserting variables. Type "/" to
 * open a filterable list of the tokens valid for this email kind; arrow keys +
 * Enter (or click) insert `{token}` at the caret. Purely additive — everything
 * else types normally.
 */
export function VariableTextarea({
  value,
  onChange,
  tokens,
  label,
  id,
  rows = 4,
  placeholder,
  disabled,
  textareaClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  tokens: TokenDef[];
  label?: string;
  id?: string;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
  textareaClassName?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [menu, setMenu] = useState<{ query: string; start: number } | null>(null);
  const [hi, setHi] = useState(0);

  const matches = menu ? filterTokens(tokens, menu.query) : [];

  /** Find an active "/query" ending at the caret (preceded by start or whitespace). */
  function detect(text: string, caret: number): { query: string; start: number } | null {
    const before = text.slice(0, caret);
    const m = /(^|\s)\/([\w-]*)$/.exec(before);
    if (!m) return null;
    return { query: m[2], start: caret - m[2].length - 1 };
  }

  function insert(name: string) {
    const el = ref.current;
    if (!el || !menu) return;
    const caret = el.selectionStart ?? value.length;
    const next = `${value.slice(0, menu.start)}{${name}}${value.slice(caret)}`;
    onChange(next);
    setMenu(null);
    const pos = menu.start + name.length + 2;
    // Restore focus + caret after the controlled re-render paints.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!menu || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insert(matches[hi].name);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMenu(null);
    }
  }

  return (
    <div className="relative">
      {label && (
        <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-600">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        onBlur={() => window.setTimeout(() => setMenu(null), 150)}
        onChange={(e) => {
          onChange(e.target.value);
          setMenu(detect(e.target.value, e.target.selectionStart ?? e.target.value.length));
          setHi(0);
        }}
        className={cn(
          'w-full rounded-2xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-800 shadow-sm transition',
          'placeholder:text-gray-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100',
          'disabled:cursor-not-allowed disabled:bg-gray-50',
          textareaClassName
        )}
      />
      {menu && matches.length === 0 && menu.query && (
        <div className="absolute z-30 mt-1 w-80 rounded-2xl border border-gray-100 bg-white p-3 text-xs text-gray-500 shadow-xl">
          No variable matches “{menu.query}”. Delete the text after “/” to see them all.
        </div>
      )}
      {menu && matches.length > 0 && (
        <div className="absolute z-30 mt-1 max-h-60 w-80 overflow-auto rounded-2xl border border-gray-100 bg-white p-1 shadow-xl">
          {matches.map((t, i) => (
            <button
              type="button"
              key={t.name}
              // mousedown (not click) + preventDefault keeps textarea focus, so the
              // insert runs before onBlur can close the menu.
              onMouseDown={(e) => {
                e.preventDefault();
                insert(t.name);
              }}
              onMouseEnter={() => setHi(i)}
              className={cn(
                'flex w-full items-start gap-2 rounded-xl px-2.5 py-2 text-left',
                i === hi ? 'bg-teal-50' : 'hover:bg-gray-50'
              )}
            >
              <span className="mt-0.5 shrink-0 rounded-full bg-teal-100 px-2 py-0.5 font-mono text-[11px] text-teal-800">
                {`{${t.name}}`}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-medium text-gray-800">{t.label}</span>
                <span className="block text-[11px] leading-snug text-gray-400">{t.desc}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      <p className="mt-1 text-[11px] text-gray-400">
        Type <kbd className="rounded bg-gray-100 px-1 font-mono">/</kbd> to insert a variable
      </p>
    </div>
  );
}
